import React, { useState } from "react";
import { base44 } from "../api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Shield, CheckCircle, Crown, Loader2, AlertTriangle } from "lucide-react";

export default function InitAdmin() {
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const queryClient = useQueryClient();

  const { data: currentUser, isLoading: userLoading } = useQuery({ queryKey:['current-user-init'], queryFn:() => base44.auth.me(), retry:false });
  const { data: allUsers = [], isLoading: usersLoading } = useQuery({ queryKey:['all-users-check'], queryFn:() => base44.entities.User.list() });
  const principalAdminExists = allUsers.some(u => u.admin_role === 'principal');

  const becomePrincipalMutation = useMutation({
    mutationFn: async () => {
      await base44.auth.updateMe({ admin_role:'principal', admin_added_by:currentUser.email, admin_added_date:new Date().toISOString() });
      await base44.entities.AdminLog.create({ action:'init_admin_principal', admin_email:currentUser.email, target_email:currentUser.email, details:`${currentUser.email} s'est auto-attribué le rôle d'administrateur principal`, timestamp:new Date().toISOString() });
    },
    onSuccess: () => { queryClient.invalidateQueries(['current-user-init']); queryClient.invalidateQueries(['all-users-check']); queryClient.invalidateQueries(['current-user']); queryClient.invalidateQueries(['current-user-layout']); setSuccess(true); setProcessing(false); },
    onError: () => { setError("Erreur lors de l'attribution du rôle"); setProcessing(false); }
  });

  if (userLoading || usersLoading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-16 h-16 text-[#c41e3a] animate-spin" /></div>;

  if (currentUser?.admin_role === 'principal') return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full p-8 text-center bg-white rounded-xl shadow-xl">
        <Crown className="w-16 h-16 text-[#c41e3a] mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-black mb-2">Vous êtes déjà Administrateur Principal</h2>
        <p className="text-gray-600 mb-6">Vous avez un accès complet à toutes les fonctionnalités.</p>
        <Link to="/dashboard"><button className="accent-gradient text-white px-6 py-3 rounded-lg hover:opacity-90 w-full font-bold">Accéder au Dashboard</button></Link>
      </div>
    </div>
  );

  if (principalAdminExists) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full p-8 text-center bg-white rounded-xl shadow-xl">
        <Shield className="w-16 h-16 text-orange-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-black mb-2">Administrateur Principal Déjà Défini</h2>
        <p className="text-gray-600 mb-6">Contactez l'administrateur principal pour obtenir des droits d'accès.</p>
        <Link to="/accueil"><button className="accent-gradient text-white px-6 py-3 rounded-lg hover:opacity-90 w-full font-bold">Retour à l'accueil</button></Link>
      </div>
    </div>
  );

  if (success) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full p-8 text-center bg-white rounded-xl shadow-xl">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-black mb-2">Félicitations !</h2>
        <p className="text-gray-600 mb-6">Vous êtes maintenant l'Administrateur Principal de R2B Entreprises.</p>
        <Link to="/dashboard"><button className="accent-gradient text-white px-6 py-3 rounded-lg hover:opacity-90 w-full font-bold">Accéder au Dashboard</button></Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-xl shadow-xl overflow-hidden">
        <div className="gradient-bg text-white p-8 text-center">
          <Crown className="w-16 h-16 mx-auto mb-4 text-yellow-400" />
          <h1 className="text-3xl font-bold">Initialisation de l'Administrateur Principal</h1>
        </div>
        <div className="p-8 space-y-6">
          <div className="bg-blue-50 border border-blue-200 flex items-center gap-2 p-4 rounded-lg"><Shield className="w-4 h-4 text-blue-600" /><span className="text-blue-800">Aucun administrateur principal n'est actuellement défini sur cette plateforme.</span></div>
          <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
            <h3 className="font-bold text-lg mb-4 text-black">Informations de votre compte :</h3>
            <div className="space-y-2">
              <div className="flex justify-between"><span className="text-gray-600">Nom :</span><span className="font-medium">{currentUser?.full_name || 'Non défini'}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Email :</span><span className="font-medium">{currentUser?.email}</span></div>
            </div>
          </div>
          <div className="bg-red-50 p-6 rounded-lg border-2 border-red-200">
            <h3 className="font-bold text-lg mb-3 text-black flex items-center gap-2"><Crown className="w-5 h-5 text-[#c41e3a]" />Privilèges de l'Administrateur Principal</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              {["Accès complet à toutes les fonctionnalités","Gestion des projets, campagnes, contributions et services","Pouvoir d'inviter et de gérer jusqu'à 5 administrateurs secondaires","Configuration complète de la plateforme","Contrôle total sur la gestion de l'équipe, du blog et des messages"].map((item,i) => (
                <li key={i} className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /><span>{item}</span></li>
              ))}
            </ul>
          </div>
          {error && <div className="bg-red-50 border border-red-200 flex items-center gap-2 p-4 rounded-lg"><AlertTriangle className="w-4 h-4 text-red-600" /><span className="text-red-800">{error}</span></div>}
          <div className="flex flex-col gap-3">
            <button onClick={() => { setProcessing(true); becomePrincipalMutation.mutate(); }} disabled={processing} className="w-full accent-gradient text-white py-4 text-lg rounded-lg hover:opacity-90 font-bold flex items-center justify-center gap-2">
              {processing ? <><Loader2 className="w-5 h-5 animate-spin" />Attribution en cours...</> : <><Crown className="w-5 h-5" />Devenir Administrateur Principal</>}
            </button>
            <Link to="/accueil"><button className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50">Annuler et revenir à l'accueil</button></Link>
          </div>
          <p className="text-xs text-gray-500 text-center">En devenant administrateur principal, vous acceptez la responsabilité de la gestion complète de la plateforme R2B Entreprises.</p>
        </div>
      </div>
    </div>
  );
}
